import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import NotificationsBell from "@/components/NotificationsBell";
import InstallAppButton from "@/components/InstallAppButton";
import { isAdmin, canAccessControl, canViewDashboard } from "@/lib/roles";
import { DEPARTMENTS } from "@/lib/departments";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Clock, ShieldCheck, Share2, Archive as ArchiveIcon, School,
  LogOut, Moon, Sun, Menu, X, User as UserIcon, Mail, Clapperboard, Home as HomeIcon,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { name: "Home", short: "Home", icon: HomeIcon, route: "/home" },
  { name: "Dashboard", short: "Dashboard", icon: LayoutDashboard, route: "/dashboard", dashboard: true },
  { name: "Daily Tracker", short: "Tracker", icon: Clock, route: "/daily-tracker" },
  { name: "Creative Workflow", short: "Workflow", icon: Clapperboard, route: "/workflow" },
  { name: "Social Media", short: "Social", icon: Share2, route: "/calendar" },
  { name: "Creative Archive", short: "Archive", icon: ArchiveIcon, route: "/archive" },
  { name: "Manage Schools", short: "Schools", icon: School, route: "/schools", accessAdmin: true },
  { name: "Access Control", short: "Access", icon: ShieldCheck, route: "/access-control", accessAdmin: true },
];

const TITLES = {
  "/dashboard": "Dashboard",
  "/workflow": "Creative Workflow",
  "/daily-tracker": "Daily Team Tracker",
  "/calendar": "Social Media",
  "/archive": "Creative Archive",
  "/schools": "Manage Schools / Field Locations",
  "/access-control": "Access Control",
  "/profile": "My Profile",
};

export default function Layout() {
  const { user, logout, viewMode, setViewMode } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isUserAdmin = isAdmin(user);
  const employeeView = isUserAdmin && viewMode === "employee";
  const admin = isUserAdmin && !employeeView;
  const accessAdmin = canAccessControl(user) && !employeeView;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(document.documentElement.classList.contains("dark"));

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const items = NAV.filter((n) => (!n.accessAdmin || accessAdmin) && (!n.dashboard || canViewDashboard(user)));
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const [digestSending, setDigestSending] = useState(false);
  const sendDigest = async (department) => {
    setDigestSending(true);
    try {
      const { data } = await api.post("/admin/digest/send-preview", null, { params: department ? { department } : {} });
      toast.success(`Daily Management Digest (${data.scope}) emailed to ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the digest");
    } finally {
      setDigestSending(false);
    }
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-2 py-1 mb-6 cursor-pointer" data-testid="brand-home" onClick={() => { navigate("/home"); setMobileOpen(false); }}>
        <div className="h-9 w-9 rounded-lg bg-white border border-border flex items-center justify-center overflow-hidden">
          <img src="/livelife-logo.webp" alt="LiveLife Education" className="h-7 w-auto object-contain" />
        </div>
        <div className="leading-none">
          <p className="font-display font-bold tracking-tight">LiveLife Education</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Intranet</p>
        </div>
      </div>
      <nav className="flex-1 space-y-1">
        {items.map((item) => (
          <NavLink
            key={item.route}
            to={item.route}
            data-testid={`nav-${item.short.toLowerCase()}`}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              }`
            }
          >
            <item.icon className="h-4.5 w-4.5 shrink-0" />
            <span>{item.name}</span>
          </NavLink>
        ))}
      </nav>
      <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground cursor-pointer hover:bg-muted/60 transition-colors mb-2" data-testid="sidebar-profile-link" onClick={() => { navigate("/profile"); setMobileOpen(false); }}>
        <p className="font-semibold text-foreground truncate">{user?.name}</p>
        <p className="truncate">{user?.designation || <span className="capitalize">{(user?.roles || [])[0]?.replace("_", " ").toLowerCase() || user?.role}</span>}</p>
        <p className="truncate">{user?.email}</p>
      </div>
      <InstallAppButton />
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-64 flex-col border-r border-border bg-card p-4 z-40">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 max-w-[80%] bg-card border-r border-border p-4">
            <button className="absolute right-3 top-3" onClick={() => setMobileOpen(false)}>
              <X className="h-5 w-5" />
            </button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 border-b border-border bg-card/80 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="font-display text-lg sm:text-xl font-bold tracking-tight truncate">
              {TITLES[location.pathname] || "LiveLife Education"}
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {isUserAdmin && (
              <div className="flex items-center rounded-full border border-border bg-muted/50 p-0.5 text-xs font-semibold" data-testid="view-mode-toggle">
                <button
                  data-testid="view-mode-admin"
                  onClick={() => { setViewMode("admin"); navigate("/home"); }}
                  className={`px-2.5 sm:px-3 py-1 rounded-full transition-colors ${!employeeView ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Admin View
                </button>
                <button
                  data-testid="view-mode-employee"
                  onClick={() => { setViewMode("employee"); navigate("/home"); }}
                  className={`px-2.5 sm:px-3 py-1 rounded-full transition-colors ${employeeView ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                  My Employee View
                </button>
              </div>
            )}
            {admin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" disabled={digestSending} data-testid="send-digest-button" title="Email the Daily Management Digest" className="rounded-full">
                    <Mail className={`h-4.5 w-4.5 ${digestSending ? "animate-pulse text-primary" : ""}`} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 max-h-[70vh] overflow-y-auto">
                  <DropdownMenuLabel>Email me the digest</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="digest-whole-org" onClick={() => sendDigest()}>
                    Whole Organisation
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[11px] text-muted-foreground font-normal">By department</DropdownMenuLabel>
                  {DEPARTMENTS.map((d) => (
                    <DropdownMenuItem key={d} data-testid={`digest-dept-${d}`} onClick={() => sendDigest(d)}>
                      {d}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <NotificationsBell />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle" className="rounded-full">
              {dark ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
            </Button>
            <div className="hidden sm:block text-right leading-tight mr-1">
              <p className="text-sm font-semibold truncate max-w-[140px]">{user?.name}</p>
              <p className="text-[11px] text-muted-foreground truncate max-w-[140px]">{user?.designation || ((user?.roles || [])[0]?.replace("_", " ").toLowerCase() || user?.role)}</p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-button" className="rounded-full">
                  <Avatar className="h-9 w-9 border border-border">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-xs text-muted-foreground font-normal">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="profile-menu" onClick={() => navigate("/profile")}>
                  <UserIcon className="h-4 w-4 mr-2" /> Profile
                </DropdownMenuItem>
                {accessAdmin && (
                  <DropdownMenuItem data-testid="access-control-menu" onClick={() => navigate("/access-control")}>
                    <ShieldCheck className="h-4 w-4 mr-2" /> Access Control
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-rose-600 focus:text-rose-600">
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
